import { forwardRef, useState } from 'react'

import styled from '@emotion/styled'

import { ArcadeIcon, SensitiveHiddenIcon, SensitiveVisibleIcon } from '@arcade/web/icons/react'

import { useHelpTextId } from '../../../hooks/useHelpTextId'
import { FieldLabelProps as ArcadeFieldLabelProps } from '../FieldLabel/ArcadeFieldLabel'
import ArcadeFieldLabel from '../FieldLabel/ArcadeFieldLabel'
import ArcadeInput, { ArcadeInputProps } from '../Input/ArcadeInput'

const StyledArcadeFieldLabel = styled(ArcadeFieldLabel)`
  &:has(input:active) {
    span {
      color: ${({ theme }) => theme.arcadeColors.component.inputTextDefault};
    }
  }

  &:has(input:focus) {
    span {
      color: ${({ theme }) => theme.arcadeColors.component.inputTextDefault};
    }
  }

  &:has(input:not(:placeholder-shown)) {
    span {
      color: ${({ theme }) => theme.arcadeColors.component.inputTextDefault};
    }
  }

  &:has(input:invalid),
  &:has(input[aria-invalid='true']) {
    .help-text,
    .disabled-text {
      display: none;
    }

    .error-text {
      display: block;
      color: ${({ theme }) => theme.arcadeColors.component.inputHelperTextError};
    }
  }

  &:has(input:disabled) {
    span {
      color: ${({ theme }) => theme.arcadeColors.component.inputTextDisabled};
    }

    .help-text,
    .error-text {
      display: none;
    }

    .disabled-text {
      display: block;
      color: ${({ theme }) => theme.arcadeColors.component.inputTextDisabled};
    }
  }
`

const InputContainer = styled.div`
  position: relative;
  width: 100%;
`

export interface MaskedInputProps
  extends
    React.InputHTMLAttributes<HTMLInputElement>,
    Pick<
      ArcadeInputProps,
      | 'helpText'
      | 'HelpTextIcon'
      | 'disabled'
      | 'disabledText'
      | 'errorText'
      | 'defaultValue'
      | 'onChange'
      | 'isInvalid'
    > {
  type?: 'password' | 'masked'
  fieldLabelProps: ArcadeFieldLabelProps
  showPasswordLabel?: string
  hidePasswordLabel?: string
}

const MaskedInput = forwardRef<HTMLInputElement, MaskedInputProps>(
  (
    {
      fieldLabelProps,
      showPasswordLabel,
      hidePasswordLabel,
      helpText,
      HelpTextIcon,
      disabled,
      disabledText,
      errorText,
      defaultValue,
      onChange,
      value,
      type = 'masked',
      isInvalid = false,
      ...restInputProps
    },
    ref
  ) => {
    const helpTextId = useHelpTextId({ helpText, errorText, disabledText })
    const [isMasked, setMasked] = useState(true)

    const showPasswordIcon = type === 'password' && !!showPasswordLabel && !!hidePasswordLabel

    return (
      <StyledArcadeFieldLabel {...fieldLabelProps}>
        <InputContainer>
          <ArcadeInput
            {...restInputProps}
            type={isMasked ? 'password' : 'text'}
            ref={ref}
            value={defaultValue ?? value}
            onChange={onChange}
            helpText={helpText}
            HelpTextIcon={HelpTextIcon}
            aria-describedby={helpTextId}
            disabled={disabled}
            disabledText={disabledText}
            isInvalid={isInvalid}
            shouldShowErrorIcon={!showPasswordIcon}
            errorText={errorText}
            TrailingIcon={
              showPasswordIcon ? ((isMasked ? SensitiveHiddenIcon : SensitiveVisibleIcon) as ArcadeIcon) : undefined
            }
            trailingIconClickProps={
              showPasswordIcon
                ? {
                    onClick: () => setMasked(!isMasked),
                    ariaLabel: isMasked ? showPasswordLabel || '' : hidePasswordLabel || '',
                  }
                : undefined
            }
          />
        </InputContainer>
      </StyledArcadeFieldLabel>
    )
  }
)

export default MaskedInput
